import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ChevronDown, ChevronUp, Plus, Trash2, Menu, Flag } from "lucide-react";
import { PageSections } from "./PageSections";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import type { Page } from "./types";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
interface SortablePageProps {
  page: Page;
  onUpdate: (updatedPage: Page) => void;
  onDelete: () => void;
  isPro: boolean;
  isFinishPage?: boolean;
}
const SortablePage = ({
  page,
  onUpdate,
  onDelete,
  isPro,
  isFinishPage
}: SortablePageProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({
    id: page.id
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };
  const handleTitleChange = (title: string) => {
    const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    onUpdate({
      ...page,
      title,
      slug
    });
  };
  return <div ref={setNodeRef} style={style} className="group border rounded-lg bg-white/50 backdrop-blur-sm shadow-sm hover:shadow-md transition-all duration-200">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="p-3 flex items-center justify-between border-b bg-slate-200 hover:bg-slate-100">
          <div className="flex items-center gap-2">
            <button {...attributes} {...listeners} className="cursor-grab hover:text-primary focus:outline-none" aria-label="Drag to reorder">
              <Menu className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
            </button>
            <Input value={page.title} onChange={e => handleTitleChange(e.target.value)} placeholder="Page Title" className="h-8 w-48" />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch id={`show-in-menu-${page.id}`} checked={page.showInMenu} onCheckedChange={checked => onUpdate({
              ...page,
              showInMenu: checked
            })} className="data-[state=checked]:bg-primary" />
              
            </div>

            {!page.isMainPage && <Button variant="ghost" size="sm" onClick={onDelete} className="text-gray-400 hover:text-red-600 transition-colors">
                <Trash2 className="w-4 h-4" />
              </Button>}

            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="p-1 h-auto">
                {isOpen ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
              </Button>
            </CollapsibleTrigger>
          </div>
        </div>
        <CollapsibleContent>
          <div className="p-3">
            <PageSections sections={page.sections} onChange={sections => onUpdate({
            ...page,
            sections
          })} isPro={isPro} />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>;
};
interface PagesListProps {
  pages: Page[];
  onChange: (pages: Page[]) => void;
  isPro: boolean;
  onPageSelect?: (pageId: string) => void;
  finishPageId?: string;
}
export const PagesList = ({
  pages,
  onChange,
  isPro,
  onPageSelect,
  finishPageId
}: PagesListProps) => {
  const MAX_PAGES = 3;
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  const addPage = () => {
    if (!isPro && pages.length >= MAX_PAGES) {
      toast.error(`Free Funnel is limited to ${MAX_PAGES} pages. Upgrade to add more!`);
      return;
    }
    const newPage: Page = {
      id: crypto.randomUUID(),
      title: "New Page",
      slug: "new-page",
      sections: [],
      showInMenu: true,
      showHeader: true,
      showFooter: true,
      order: pages.length
    };
    onChange([...pages, newPage]);
  };
  const updatePage = (id: string, updatedPage: Page) => {
    onChange(pages.map(page => page.id === id ? updatedPage : page));
  };
  const deletePage = (id: string) => {
    onChange(pages.filter(page => page.id !== id));
  };
  const handleDragEnd = (event: any) => {
    const {
      active,
      over
    } = event;
    if (active.id !== over.id) {
      const oldIndex = pages.findIndex(page => page.id === active.id);
      const newIndex = pages.findIndex(page => page.id === over.id);
      onChange(arrayMove(pages, oldIndex, newIndex).map((page, index) => ({
        ...page,
        order: index
      })));
    }
  };
  const handlePageClick = (page: Page) => {
    if (onPageSelect) {
      onPageSelect(page.id);
    }
  };
  return <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Oldalak</h3>
          <p className="text-sm text-muted-foreground">
            {isPro ? "Add as many pages as you need" : `${pages.length}/${MAX_PAGES} pages used`}
          </p>
        </div>
        <Button onClick={addPage} size="sm" className="text-slate-50 rounded-full">
          <Plus className="w-4 h-4 mr-2" />
          Add Page
        </Button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={pages.map(page => page.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {pages.map(page => <div key={page.id} onClick={() => handlePageClick(page)}>
                <SortablePage page={page} onUpdate={updatedPage => updatePage(page.id, updatedPage)} onDelete={() => deletePage(page.id)} isPro={isPro} isFinishPage={finishPageId === page.id} />
              </div>)}
          </div>
        </SortableContext>
      </DndContext>
    </div>;
};